import { Button, Card, CardContent, CardHeader, CardTitle } from "@exhousing/design-system";
import { Loader2, MessageSquareQuote, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useClearSelectionAdvice,
  useProperty,
  useSaveSelectionAdvice,
} from "@/hooks/api/useProperties";

/**
 * Journey 7.1: Sophie's selection advice to the landlord. Staff write it here (the
 * landlord gets a mail with a deep link); landlords see it read-only next to the
 * comparison table. Cleared automatically when a fallback re-opens the round.
 */
export function AdviceCard({
  propertyId,
  readOnly,
}: {
  propertyId: string;
  readOnly: boolean;
}) {
  const { data: property } = useProperty(propertyId);
  const save = useSaveSelectionAdvice();
  const clear = useClearSelectionAdvice();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (!property) return null;
  const advice = property.selection_advice;
  if (readOnly && !advice) return null;

  const startEdit = () => {
    setDraft(advice ?? "");
    setEditing(true);
  };
  const submit = () =>
    save.mutate(
      { propertyId, advice: draft.trim() },
      {
        onSuccess: () => {
          toast.success("Advice sent to the landlord");
          setEditing(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );

  return (
    <Card className="border-accent/30">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquareQuote className="size-4" /> Selection advice
        </CardTitle>
        {!readOnly && !editing && (
          <div className="flex gap-2">
            {advice && (
              <Button
                size="sm"
                variant="ghost"
                disabled={clear.isPending}
                onClick={() =>
                  clear.mutate(propertyId, {
                    onSuccess: () => toast.success("Advice cleared"),
                    onError: (err) => toast.error(err.message),
                  })
                }
              >
                <Trash2 className="size-4" /> Clear
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={startEdit}>
              {advice ? "Edit advice" : "Write advice"}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-3">
            <textarea
              rows={4}
              maxLength={4000}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Your recommendation to the landlord — which candidate, and on which objective grounds."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={save.isPending || !draft.trim()} onClick={submit}>
                {save.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                Send to landlord
              </Button>
            </div>
          </div>
        ) : advice ? (
          <>
            <p className="whitespace-pre-line text-sm">{advice}</p>
            {property.selection_advice_at && (
              <p className="mt-2 text-xs text-muted-foreground">
                {new Date(property.selection_advice_at).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No advice yet. Once the finalists are approved, write your
            recommendation — the landlord is notified by e-mail.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
